import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import grid from "fantastic/lib/grid";
import connectProps from "fantastic/lib/connectProps";

const SectionColumnResizer = ({
  className = "",
  onResizeStart = () => {},
  onResize = (prev, next) => {},
  onResizeStop = (prev, next) => {},
  theme: { style_attributes }
}) => {
  const [dragging, setDragging] = useState(false);
  const [mouseX, setMouseX] = useState(0);
  const [startPos, setStartPos] = useState(0);
  const [clientWidthPrev, setClientWidthPrev] = useState(0);
  const [clientWidthNext, setClientWidthNext] = useState(0);
  const [maxWidth, setMaxWidth] = useState(0);
  const [minWidth, setMinWidth] = useState(0);

  const resizerClass = classNames(className, {
    "fantastic-column__resizer--active": dragging
  });

  const ele = useRef(null);

  const endDrag = () => {
    if (!dragging) return;

    setDragging(false);
    const elm = ele.current;
    elm.previousSibling.children[0].style.width = null;
    elm.nextSibling.children[0].style.width = null;
    onResizeStop();
  };

  const onMouseMove = e => {
    let mouse = e.touches ? e.touches[0].screenX : e.screenX;
    setMouseX(mouse);
    if (!dragging) return;

    const elm = ele.current;

    const moveDiff = startPos - mouse;
    let newPrev = clientWidthPrev - moveDiff;

    if (newPrev < minWidth) {
      newPrev = minWidth;
    }

    if (newPrev > maxWidth) {
      newPrev = maxWidth;
    }

    let prevWord = grid.size2word(newPrev - 20);
    let prevSize = grid.word2size(prevWord);

    let nextWord = grid.size2word(maxWidth - prevSize);
    let nextSize = grid.word2size(nextWord);

    elm.previousSibling.children[0].style.width = prevSize + "px";
    elm.nextSibling.children[0].style.width = nextSize + "px";

    onResize(prevWord, nextWord);
  };

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchmove", onMouseMove);
    document.addEventListener("touchend", endDrag);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchmove", onMouseMove);
      document.removeEventListener("touchend", endDrag);
    };
  });

  const startDrag = () => {
    setDragging(true);
    setStartPos(mouseX);

    let widthPrev = 0;
    let widthNext = 0;
    let prevWord = "";
    let nextWord = "";

    const elm = ele.current;

    if (elm) {
      let prevSibling = elm.previousSibling;
      let nextSibling = elm.nextSibling;

      if (prevSibling) {
        widthPrev = prevSibling.clientWidth;
        prevWord = [...prevSibling.children[0].classList].filter(c =>
          grid.gridWords.includes(c)
        )[0];
      }

      if (nextSibling) {
        widthNext = nextSibling.clientWidth;
        nextWord = [...nextSibling.children[0].classList].filter(c =>
          grid.gridWords.includes(c)
        )[0];
      }
    }

    setClientWidthPrev(widthPrev);
    setClientWidthNext(widthNext);

    setMinWidth(grid.word2size("one"));
    if (prevWord.length + nextWord.length > 0) {
      setMaxWidth(grid.word2size(prevWord) + grid.word2size(nextWord));
    }

    onResizeStart();
  };

  const style = {
    userSelect: "none",
    width: 0,
    cursor: "ew-resize"
  };

  return (
    <td
      ref={ele}
      style={style}
      className={resizerClass}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
    />
  );
};

export default connectProps("theme")(SectionColumnResizer);
